import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from '@capacitor/push-notifications';
import { PLANBASE_CONFIG } from '../config';

/**
 * usePushNotifications
 *
 * Registers for push notifications on native iOS/Android.
 * - Requests permission on first run
 * - Sends the FCM/APNs device token to your backend
 * - Handles foreground notifications (shows a toast via sonner)
 * - Handles notification tap (action performed)
 *
 * Call this once at the app root level (inside AuthProvider, after user is known).
 */
export function usePushNotifications(userId?: string) {
  const registered = useRef(false);

  useEffect(() => {
    // Only run on native iOS/Android
    if (!Capacitor.isNativePlatform()) return;
    // Only register once we have a logged-in user
    if (!userId) return;
    // Only register once per session
    if (registered.current) return;
    registered.current = true;

    registerPush(userId);

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [userId]);
}

async function registerPush(userId?: string) {
  // 1. Check / request permission
  let permStatus = await PushNotifications.checkPermissions();

  if (permStatus.receive === 'prompt') {
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive !== 'granted') {
    console.warn('[Push] Permission not granted');
    return;
  }

  // 2. Register with APNs (iOS) or FCM (Android)
  await PushNotifications.register();

  // 3. Received the device token — send to backend
  await PushNotifications.addListener('registration', async (token: Token) => {
    console.log('[Push] Device token:', token.value);

    if (!userId) return;

    try {
      await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/push/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('planbase_token') ?? ''}`,
        },
        body: JSON.stringify({
          project_id: PLANBASE_CONFIG.projectId,
          user_id: userId,
          token: token.value,
          platform: Capacitor.getPlatform(), // 'ios' or 'android'
        }),
      });
    } catch (err) {
      console.warn('[Push] Failed to register token with backend', err);
    }
  });

  // 4. Registration error
  await PushNotifications.addListener('registrationError', (err) => {
    console.error('[Push] Registration error:', err.error);
  });

  // 5. Notification received while app is in foreground
  await PushNotifications.addListener(
    'pushNotificationReceived',
    (notification: PushNotificationSchema) => {
      console.log('[Push] Foreground notification:', notification);

      // Dynamically import sonner to show a toast
      import('sonner').then(({ toast }) => {
        toast(notification.title ?? 'Notification', {
          description: notification.body,
          duration: 5000,
        });
      });
    },
  );

  // 6. User tapped a notification
  await PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action: ActionPerformed) => {
      console.log('[Push] Notification tapped:', action.notification);

      // Navigate based on data payload if present
      const data = action.notification.data;
      if (data?.url) {
        window.location.hash = data.url;
      }
    },
  );
}
